import React, { Component } from "react";
import PropTypes from "prop-types";
import { connect } from "react-redux";

import ReadOnlyWrapper from "editor/containers/ReadOnlyWrapper";
import Spinner from "components/library/Spinner";
import ImageUploader from "./ImageUploader";
import ImageCropper from "./ImageCropper";
import ImageMeta from "./ImageMeta";
import MessagePanel from "components/library/MessagePanel";
import { uploadImageAsync, updateImageAsync } from "./actions/imageEditor";
import { setMessage } from "actions/message";
import defaultImageValidator from "./utils/defaultImageValidator";
import defaultUploadHelpText from "./utils/defaultUploadHelpText";
import { MESSAGES } from "config/constants";

import styles from "./ImageEditor.css";

class ImageEditor extends Component {
  constructor(props) {
    super(props);

    this.closeEditor = this._closeEditor.bind(this);
    this.nextStep = this._nextStep.bind(this);
    this.uploadImage = this._uploadImage.bind(this);
    this.updateImage = this._updateImage.bind(this);
    this.submitImage = this._submitImage.bind(this);
    this.setInvalid = this._setInvalid.bind(this);
    this.setLoadingState = this._setLoadingState.bind(this);
    this.setSuggestedAltText = this._setSuggestedAltText.bind(this);

    this.state = {
      currentStep: "upload",
      isLoading: false,
      image: null,
      errors: {},
      suggestedAltText: "",
    };
  }

  _closeEditor() {
    this.props.history.go(-1);
  }

  _nextStep() {
    switch (this.state.currentStep) {
      case "upload":
        this.setState({ currentStep: "crop", isLoading: false });
        break;
      case "crop":
        this.setState({ currentStep: "meta" });
        break;
      case "meta":
        this.props.history.push(this.props.redirectTo);
        break;
    }
  }

  _setInvalid(message) {
    this.props.setMessage({ message, type: MESSAGES.failure });
    switch (this.state.currentStep) {
      case "crop":
        this.setState({ currentStep: "upload" });
        break;
    }
  }

  _setLoadingState(isLoading) {
    this.setState({ isLoading });
  }

  _uploadImage(uploadParams) {
    return this.props.uploadImageAsync(uploadParams).then(action => {
      this.setState({ image: action.payload });
    });
  }

  _updateImage(imageId, updateParams) {
    return this.props
      .updateImageAsync(imageId, updateParams)
      .then(action => {
        this.setState({ image: action.payload });
      })
      .catch(error => {
        // Handle error API to manage error state for child components
        if ("context" in error) {
          this.setState({ errors: error.context.decodedJSON });
          throw error; // Ensure this.nextStep isn't called by children
        }
      });
  }

  _setSuggestedAltText(altText) {
    this.setState({ suggestedAltText: altText });
  }

  _submitImage() {
    return this.props.onSuccess(this.state.image);
  }

  render() {
    const {
      hideCropNames,
      skipCropping,
      skipMeta,
      imageRequirements,
      cropRequirements,
      imageValidator,
      setMessage,
      uploadHelpText,
    } = this.props;

    if (this.state.isLoading) {
      return (
        <div className={styles.root}>
          <MessagePanel />
          <div className={styles.loading}>
            <Spinner />
          </div>
        </div>
      );
    }

    return (
      <ReadOnlyWrapper
        id={this.props.match.params.id}
        status={this.props.form.status}
      >
        <MessagePanel />
        <div className={styles.root}>
          <div className={styles.container}>
            {this.state.currentStep === "upload" && (
              <ImageUploader
                history={this.props.history}
                imageRequirements={imageRequirements}
                nextStep={this.nextStep}
                onSubmit={this.uploadImage}
                setLoadingState={this.setLoadingState}
                setMessage={setMessage}
                setSuggestedAltText={this.setSuggestedAltText}
                uploadHelpText={uploadHelpText}
              />
            )}
            {this.state.currentStep === "crop" && (
              <ImageCropper
                history={this.props.history}
                image={this.state.image}
                setCrops={crops =>
                  this.updateImage(this.state.image.id, { crops })
                }
                nextStep={this.nextStep}
                skip={skipCropping}
                imageRequirements={imageRequirements}
                cropRequirements={cropRequirements}
                imageValidator={imageValidator}
                setInvalid={this.setInvalid}
                setLoadingState={this.setLoadingState}
                hideCropNames={hideCropNames}
              />
            )}
            {this.state.currentStep === "meta" && (
              <ImageMeta
                history={this.props.history}
                addMeta={meta => this.updateImage(this.state.image.id, meta)}
                submitImage={this.submitImage}
                thumbnail={this.state.image.crops["1x1"].url}
                nextStep={this.nextStep}
                suggestedAltText={this.state.suggestedAltText}
                skip={skipMeta}
                setLoadingState={this.setLoadingState}
                errors={this.state.errors}
              />
            )}
          </div>
        </div>
      </ReadOnlyWrapper>
    );
  }
}

const mapStateToProps = state => ({
  form: state.editor.form,
});

const mapDispatchToProps = {
  uploadImageAsync,
  updateImageAsync,
  setMessage,
};

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(ImageEditor);

ImageEditor.defaultProps = {
  imageRequirements: {},
  cropRequirements: {
    "16x9": {},
    "4x3": {},
    "1x1": {},
  },
  imageValidator: defaultImageValidator,
  uploadHelpText: defaultUploadHelpText,
  hideCropNames: false,
};

ImageEditor.propTypes = {
  history: PropTypes.shape({
    go: PropTypes.func.isRequired,
    push: PropTypes.func.isRequired,
  }).isRequired,
  setMessage: PropTypes.func.isRequired,
  uploadImageAsync: PropTypes.func.isRequired,
  updateImageAsync: PropTypes.func.isRequired,
  redirectTo: PropTypes.string.isRequired,
  onSuccess: PropTypes.func.isRequired,
  skipCropping: PropTypes.bool,
  skipMeta: PropTypes.bool,
  imageRequirements: PropTypes.shape({
    minWidth: PropTypes.number,
    minHeight: PropTypes.number,
  }),
  cropRequirements: PropTypes.shape({
    "1x1": PropTypes.object,
    "4x3": PropTypes.object,
    "16x9": PropTypes.object,
  }),
  hideCropNames: PropTypes.bool,
  imageValidator: PropTypes.func,
  uploadHelpText: PropTypes.func,
};
